import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { RoleSelect } from './RoleSelect';
import { UsersIcon, CalendarIcon, BarChartIcon } from 'lucide-react';
export function Login() {
  const {
    login,
    isAuthenticated,
    demoLogin
  } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRoleSelect, setShowRoleSelect] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const success = await login(username, password);
      if (success) {
        setShowRoleSelect(true);
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };
  const handleDemoLogin = (role: 'instructor' | 'planner' | 'manager') => {
    demoLogin(role);
  };
  if (isAuthenticated && showRoleSelect) {
    return <RoleSelect data-id="element-226" />;
  }
  return <div className="min-h-screen flex items-center justify-center bg-gray-50" data-id="element-227">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow" data-id="element-228">
        <div className="text-center" data-id="element-229">
          <div className="mx-auto h-20 w-auto" data-id="element-230">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vML8uoGZWQx9SMCPgucRCS/VVGN_Logo_Dark.png" alt="VVGN Logo" className="h-full mx-auto" data-id="element-231" />
          </div>
          <h2 className="mt-4 text-3xl font-extrabold text-gray-900" data-id="element-232">
            Operations Platform
          </h2>
          <p className="mt-2 text-sm text-gray-600" data-id="element-233">
            Verkeersveiligheid Groep Nederland
          </p>
        </div>
        {/* Demo Login Section */}
        <div className="border-b border-gray-300 pb-6" data-id="element-234">
          <h3 className="text-lg font-medium text-gray-900 mb-4 text-center" data-id="element-235">
            Demo Login
          </h3>
          <div className="grid grid-cols-1 gap-3" data-id="element-236">
            <button onClick={() => handleDemoLogin('instructor')} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B61C1C] focus:border-transparent transition group" data-id="element-237">
              <div className="flex items-center" data-id="element-238">
                <div className="bg-green-100 p-3 rounded-lg group-hover:bg-green-200 transition" data-id="element-239">
                  <UsersIcon className="h-6 w-6 text-green-600" data-id="element-240" />
                </div>
                <div className="ml-4 text-left" data-id="element-241">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-242">
                    Login als Instructeur
                  </h3>
                  <p className="text-xs text-gray-500" data-id="element-243">Instructeur@demo.com</p>
                </div>
              </div>
            </button>
            <button onClick={() => handleDemoLogin('planner')} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B61C1C] focus:border-transparent transition group" data-id="element-244">
              <div className="flex items-center" data-id="element-245">
                <div className="bg-blue-100 p-3 rounded-lg group-hover:bg-blue-200 transition" data-id="element-246">
                  <CalendarIcon className="h-6 w-6 text-blue-600" data-id="element-247" />
                </div>
                <div className="ml-4 text-left" data-id="element-248">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-249">
                    Login als Planner
                  </h3>
                  <p className="text-xs text-gray-500" data-id="element-250">Planner@demo.com</p>
                </div>
              </div>
            </button>
            <button onClick={() => handleDemoLogin('manager')} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B61C1C] focus:border-transparent transition group" data-id="element-251">
              <div className="flex items-center" data-id="element-252">
                <div className="bg-purple-100 p-3 rounded-lg group-hover:bg-purple-200 transition" data-id="element-253">
                  <BarChartIcon className="h-6 w-6 text-purple-600" data-id="element-254" />
                </div>
                <div className="ml-4 text-left" data-id="element-255">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-256">
                    Login als Manager
                  </h3>
                  <p className="text-xs text-gray-500" data-id="element-257">Manager@demo.com</p>
                </div>
              </div>
            </button>
          </div>
        </div>
        {/* Regular Login Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-id="element-258">
          <h3 className="text-lg font-medium text-gray-900 mb-4 text-center" data-id="element-259">
            Reguliere Login
          </h3>
          {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm" data-id="element-260">
              {error}
            </div>}
          <div className="rounded-md shadow-sm -space-y-px" data-id="element-261">
            <div data-id="element-262">
              <label htmlFor="username" className="sr-only" data-id="element-263">
                Username
              </label>
              <input id="username" name="username" type="text" autoComplete="username" required className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] focus:z-10 sm:text-sm" placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} data-id="element-264" />
            </div>
            <div data-id="element-265">
              <label htmlFor="password" className="sr-only" data-id="element-266">
                Password
              </label>
              <input id="password" name="password" type="password" autoComplete="current-password" required className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] focus:z-10 sm:text-sm" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} data-id="element-267" />
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-268">
            <div className="flex items-center" data-id="element-269">
              <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-[#B61C1C] focus:ring-[#B61C1C] border-gray-300 rounded" data-id="element-270" />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900" data-id="element-271">
                Remember me
              </label>
            </div>
            <div className="text-sm" data-id="element-272">
              <a href="#" className="font-medium text-[#B61C1C] hover:text-[#961818]" data-id="element-273">
                Forgot your password?
              </a>
            </div>
          </div>
          <div data-id="element-274">
            <Button type="submit" variant="primary" className="w-full" disabled={loading} data-id="element-275">
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </div>
        </form>
      </div>
    </div>;
}